import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import type { BeneficiaryAndApiHeaders } from "../../@types";
import { routes } from "../../app/routes";
import { beneKeysMatch } from "../../helpers/beneficiaryInfoHelpers";
import { devDebug } from "../../helpers/loggingHelpers";
import { StaleTime } from "../../helpers/query-hooks/types";
import { useAppState } from "../../helpers/context-hooks/useAppState";

export const useSessionHandlerRoutes = () => {
  const location = useLocation();
  const isLoginCallbackRoute = location.pathname === routes.slsCallback;
  const isMbpLandingRoute = location.pathname === routes.mbpLandingPage;
  const isLogoutRoute = location.pathname === routes.logout;
  const isCsrGuestAccessRoute = location.pathname === routes.csrGuestAccess;
  const isAnonLandingRoute = location.pathname === routes.anonLanding;
  /*
   * `routeShouldPreventCsrGuestAccess` should be set in `AppState` after the
   * first time landing on the `CSRGuestAccessPage`
   */
  const routeShouldPreventCsrGuestAccess = !(
    [routes.csrGuestAccess, routes.logout] as string[]
  ).includes(location.pathname);

  return {
    isLoginCallbackRoute,
    isMbpLandingRoute,
    isLogoutRoute,
    isCsrGuestAccessRoute,
    isAnonLandingRoute,
    routeShouldPreventCsrGuestAccess,
  };
};

interface UseBeneSessionStateParams {
  beneInfoWithHeaders: BeneficiaryAndApiHeaders | undefined;
  isLoginCallbackRoute: boolean;
}

export const useBeneSessionState = ({
  beneInfoWithHeaders,
  isLoginCallbackRoute,
}: UseBeneSessionStateParams) => {
  const { beneficiary: stateBene } = useAppState();
  const hasStateBene = !!stateBene;
  /**
   * If `fetchedBene` is defined, so is `beneInfoWithHeaders`
   */
  const fetchedBene = beneInfoWithHeaders?.beneficiary;
  const hasStateAndFetchedBene = !!stateBene && !!fetchedBene;
  /**
   * `beneHasChanged` would be `true`, e.g., when a proxy access user switches bene
   * profiles. It indicates that beneinfo was fetched before and set in `AppState`,
   * but the latest fetch returned a bene with a different key
   */
  const beneHasChanged =
    hasStateAndFetchedBene && !beneKeysMatch(stateBene, fetchedBene);
  /**
   * `beneNotChanged` indicates that beneinfo has been fetched and set in `AppState`,
   * and no subsequent fetch has returned a bene with a different key
   */
  const beneNotChanged =
    hasStateAndFetchedBene && beneKeysMatch(stateBene, fetchedBene);
  /**
   * `shouldUpdateStateWithFetchedBene` means beneinfo has already been fetched and
   * saved, and we should simply refetch to update on page load, anytime after login.
   * It would be `false` if, e.g., a proxy user switched bene profiles (meaning
   * we need to wipe and start fresh).
   */
  const shouldUpdateStateWithFetchedBene =
    !isLoginCallbackRoute && beneNotChanged;

  return {
    hasStateBene,
    fetchedBene,
    beneHasChanged,
    shouldUpdateStateWithFetchedBene,
  };
};

interface UseBeneChangeQueryInvalidationParams {
  beneHasChanged: boolean;
  skipHandling: boolean;
}

export const useBeneChangeQueryInvalidation = ({
  beneHasChanged,
  skipHandling,
}: UseBeneChangeQueryInvalidationParams) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (skipHandling || !beneHasChanged) {
      return;
    }
    queryClient.invalidateQueries({
      predicate: query => {
        // Check if the query has at least one active observer
        // and grab the staleTime from its options
        const hasObservers = query.observers.length > 0;
        const staleTimeInfinite =
          hasObservers &&
          query.observers.every(
            observer => observer.options.staleTime === StaleTime.Infinite
          );
        const queryHasFiniteStaleTime = hasObservers && !staleTimeInfinite;
        devDebug(
          `${query.queryKey} will${queryHasFiniteStaleTime ? "" : " not"} be 
          invalidated by GlobalSessionHandler because ${
            hasObservers
              ? `staleTime 
            ${staleTimeInfinite ? "" : " not"} infinite`
              : "no observers"
          }`
        );
        return queryHasFiniteStaleTime;
      },
    });
  }, [beneHasChanged, queryClient, skipHandling]);
};
